import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectDto } from '../../api/model';
import { useGetCheckpointProjectProjectId } from '../../api/check-point-api/check-point-api';
import {
  getGetActionpointProjectIdProjectQueryKey,
  useGetActionpointProjectIdProject,
} from '../../api/action-point-api/action-point-api';
import { useGetProjectIdMembers } from '../../api/project-api/project-api';
import { useProjectDeliverables } from '../../hooks/useProjectDeliverables';
import { useProjectPhases } from '../../hooks/useProjectPhases';
import { useProjectTasks } from '../../hooks/useProjectTasks';
import type { ObjectTarget } from '../ReportMetrics/detail';
import EditTaskModal from '../Tasks/EditTaskModal';
import EditActionPointModal from '../ActionPoints/EditActionPointModal';
import CheckpointDetailModal from '../CheckPlan/CheckpointDetailModal';
import DeliverableDetailPopup from '../Deliverables/components/DeliverableDetailPopup';
import { deliverableTableRows } from '../Deliverables/deliverableRows';

interface ProjectObjectDialogProps {
  project?: ProjectDto;
  /** The object whose detail is open, or none. */
  target?: ObjectTarget;
  onClose: () => void;
  /** Opens another object's detail in place of this one (a deliverable's task, say). */
  onOpen: (target: ObjectTarget) => void;
}

const startsWith = (prefix: string) => (query: { queryKey: readonly unknown[] }) =>
  typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith(prefix);

/**
 * The detail dialog of a task, deliverable, checkpoint or action point, opened
 * over whatever page of the project is showing. A link to an object - from
 * Project Intelligence, a deliverable's task list, a notification - opens just
 * this dialog and leaves the page underneath alone, so closing it returns to
 * exactly where the user was.
 *
 * Only the data the open dialog needs is fetched; these are the same queries
 * the project's pages use, so they are usually already cached.
 */
const ProjectObjectDialog: React.FC<ProjectObjectDialogProps> = ({ project, target, onClose, onOpen }) => {
  const { t } = useTranslation('checkPlan');
  const queryClient = useQueryClient();
  const projectId = project?.id ?? 0;
  const kind = target?.kind;

  const needsTasks = kind === 'task' || kind === 'checkpoint';
  const { tasks } = useProjectTasks(needsTasks ? project : undefined);
  const { deliverables } = useProjectDeliverables(kind ? project : undefined);
  const { data: phases = [] } = useProjectPhases(kind === 'task' || kind === 'deliverable' ? project?.id : undefined);
  const { data: members } = useGetProjectIdMembers(projectId, {
    query: { enabled: kind === 'deliverable' && !!projectId },
  });
  const { data: checkpoints } = useGetCheckpointProjectProjectId(projectId, {
    query: { enabled: kind === 'checkpoint' && !!projectId },
  });
  const { data: actionPoints } = useGetActionpointProjectIdProject(projectId, {
    query: { enabled: kind === 'actionPoint' && !!projectId },
  });

  const deliverableRows = useMemo(
    () => (kind === 'deliverable' ? deliverableTableRows(deliverables, phases, members) : []),
    [kind, deliverables, phases, members],
  );

  if (!target) return null;

  switch (target.kind) {
    case 'task': {
      const task = tasks.find((candidate) => candidate.id === target.id);
      if (!task) return null;
      const refresh = async () => {
        onClose();
        await queryClient.invalidateQueries({ queryKey: ['/task'] });
        await queryClient.invalidateQueries({ predicate: startsWith('/checkpoint') });
        await queryClient.invalidateQueries({ predicate: startsWith('/deliver-object') });
      };
      return (
        <EditTaskModal
          isOpen
          onClose={onClose}
          onTaskUpdated={refresh}
          onTaskDeleted={refresh}
          task={task}
          phases={phases}
          projectId={project?.id}
        />
      );
    }

    case 'deliverable': {
      const deliverable = deliverableRows.find((row) => row.id === String(target.id));
      if (!deliverable) return null;
      return (
        <DeliverableDetailPopup
          isOpen
          onClose={onClose}
          deliverable={deliverable}
          projectDeliverables={deliverableRows}
          rawDeliverables={deliverables}
          projectId={project?.id}
          project={project}
          onNavigateToTask={(taskId) => onOpen({ kind: 'task', id: taskId })}
          onDeliverableUpdate={() => {
            queryClient.invalidateQueries({ predicate: startsWith('/deliver-object') });
            if (project?.id) {
              queryClient.invalidateQueries({ queryKey: [`/phase/${project.id}/project`] });
            }
          }}
        />
      );
    }

    case 'checkpoint': {
      const checkpoint = checkpoints?.find((candidate) => candidate.id === target.id);
      if (!checkpoint) return null;
      const task = tasks.find((candidate) => candidate.id === checkpoint.taskId);
      const deliverObjectId = task?.deliverObjectId;
      return (
        <CheckpointDetailModal
          isOpen
          onClose={onClose}
          onSave={onClose}
          checkpoint={{
            id: `checkpoint-${checkpoint.taskId}`,
            taskId: checkpoint.taskId ?? 0,
            checkpointId: checkpoint.id ?? 0,
            taskName: task?.name || t('states.unnamedTask'),
            taskDescription: task?.description || t('states.noDescription'),
            status: checkpoint.status,
            responsibleRole: checkpoint.responsibleRole,
            validationCriteria: checkpoint.validationCriteria,
            acceptanceCriteria: checkpoint.acceptanceCriteria,
            validationMethod: checkpoint.validationMethod,
            checkpoint,
            task,
            deliverObjectId,
            deliverObjectName: deliverables.find((d) => d.id === deliverObjectId)?.name,
          }}
        />
      );
    }

    case 'actionPoint': {
      const actionPoint = actionPoints?.find((candidate) => candidate.id === target.id);
      if (!actionPoint) return null;
      return (
        <EditActionPointModal
          isOpen
          onClose={onClose}
          onActionPointUpdated={async () => {
            onClose();
            await queryClient.invalidateQueries({ queryKey: getGetActionpointProjectIdProjectQueryKey(projectId) });
          }}
          actionPointData={actionPoint}
        />
      );
    }
  }
};

export default ProjectObjectDialog;
